import { useCallback, useRef, useEffect } from "react";

export const usePrintInvoice = () => {
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  const cleanupPrintFrame = useCallback(() => {
    if (
      printFrameRef.current &&
      document.body.contains(printFrameRef.current)
    ) {
      document.body.removeChild(printFrameRef.current);
      printFrameRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupPrintFrame(), [cleanupPrintFrame]);

  const handlePrint = useCallback(
    (invoiceRef: HTMLDivElement) => {
      cleanupPrintFrame();

      const printFrame = document.createElement("iframe");
      printFrame.style.position = "fixed";
      printFrame.style.right = "-9999px";
      printFrame.style.bottom = "-9999px";
      printFrameRef.current = printFrame;
      document.body.appendChild(printFrame);

      const frameDoc =
        printFrame.contentDocument || printFrame.contentWindow?.document;
      if (!frameDoc) {
        // oxlint-disable-next-line no-console
        console.error("Could not access print frame document");
        return;
      }

      const styles = Array.from(document.styleSheets)
        .map((styleSheet) => {
          try {
            return styleSheet.cssRules
              ? Array.from(styleSheet.cssRules)
                  .map((rule) => rule.cssText)
                  .join("")
              : "";
          } catch {
            return "";
          }
        })
        .join("");

      frameDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${"Invoice"}</title>
          <style>
            ${styles}
            @media print {
              body {
                padding: 20mm;
                margin: 0;
                background: white;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              @page {
                size: A4;
                margin: 0;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          ${invoiceRef.outerHTML}
        </body>
      </html>
    `);
      frameDoc.close();

      if (printFrame.contentWindow) {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      }
      // });

      const cleanup = () => {
        cleanupPrintFrame();
        window.removeEventListener("focus", cleanup);
      };
      window.addEventListener("focus", cleanup);
    },
    [cleanupPrintFrame]
  );

  return {
    printInvoice: handlePrint,
  };
};
